import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Filter,
  Flame,
  Snowflake,
  Sparkles,
  ChevronDown,
  ArrowLeftRight,
  Upload,
  CloudUpload,
  X,
  EyeOff,
} from "lucide-react";

// Static UI for now — Convo Track is a new, frontend-only area (same
// pattern as the rest of this suite). There's no real attribution backend
// yet, so every filter below is backed by its own small hardcoded dataset
// instead of one frozen set of numbers — picking a different date range,
// conversion event or attribution model visibly changes what's on screen,
// even though none of it is live data.

type ChannelColor = "indigo" | "green" | "olive" | "cyan";

const COLOR_BAR: Record<ChannelColor, string> = {
  indigo: "bg-indigo-500",
  green: "bg-emerald-500",
  olive: "bg-lime-600",
  cyan: "bg-cyan-500",
};

interface ChannelRow {
  id: string;
  label: string;
  color: ChannelColor;
  pct: number;
  count: number;
}

type DateRange = "Last 7 days" | "Last 30 days" | "Last 90 days" | "Year to date";
type Model = "Time decay" | "First touch" | "Last touch" | "Linear";
type ConversionEvent = "Order completed" | "Signup" | "Trial started" | "Add to cart";

const DATE_RANGES: DateRange[] = ["Last 7 days", "Last 30 days", "Last 90 days", "Year to date"];
const MODELS: Model[] = ["Time decay", "First touch", "Last touch", "Linear"];
const CONVERSION_EVENTS: ConversionEvent[] = ["Order completed", "Signup", "Trial started", "Add to cart"];

// Base channel mix per date range — wider windows accumulate more volume
// and lean a little more on paid/organic vs. the shorter windows.
const DATE_RANGE_CHANNELS: Record<DateRange, ChannelRow[]> = {
  "Last 7 days": [
    { id: "paid-social", label: "Paid Social", color: "indigo", pct: 24, count: 120 },
    { id: "whatsapp-campaign", label: "WhatsApp campaign", color: "green", pct: 18, count: 90 },
    { id: "organic-search", label: "Organic search", color: "olive", pct: 16, count: 80 },
    { id: "paid-search", label: "Paid search", color: "indigo", pct: 14, count: 70 },
    { id: "email-nudge", label: "Email Nudge", color: "green", pct: 12, count: 60 },
    { id: "direct", label: "Direct / unattributed", color: "cyan", pct: 16, count: 80 },
  ],
  "Last 30 days": [
    { id: "paid-social", label: "Paid Social", color: "indigo", pct: 28, count: 601 },
    { id: "whatsapp-campaign", label: "WhatsApp campaign", color: "green", pct: 19, count: 408 },
    { id: "organic-search", label: "Organic search", color: "olive", pct: 15, count: 322 },
    { id: "paid-search", label: "Paid search", color: "indigo", pct: 13, count: 279 },
    { id: "email-nudge", label: "Email Nudge", color: "green", pct: 11, count: 236 },
    { id: "direct", label: "Direct / unattributed", color: "cyan", pct: 14, count: 300 },
  ],
  "Last 90 days": [
    { id: "paid-social", label: "Paid Social", color: "indigo", pct: 30, count: 1850 },
    { id: "whatsapp-campaign", label: "WhatsApp campaign", color: "green", pct: 20, count: 1233 },
    { id: "organic-search", label: "Organic search", color: "olive", pct: 14, count: 863 },
    { id: "paid-search", label: "Paid search", color: "indigo", pct: 12, count: 740 },
    { id: "email-nudge", label: "Email Nudge", color: "green", pct: 10, count: 617 },
    { id: "direct", label: "Direct / unattributed", color: "cyan", pct: 14, count: 863 },
  ],
  "Year to date": [
    { id: "paid-social", label: "Paid Social", color: "indigo", pct: 32, count: 7400 },
    { id: "whatsapp-campaign", label: "WhatsApp campaign", color: "green", pct: 21, count: 4856 },
    { id: "organic-search", label: "Organic search", color: "olive", pct: 13, count: 3006 },
    { id: "paid-search", label: "Paid search", color: "indigo", pct: 11, count: 2543 },
    { id: "email-nudge", label: "Email Nudge", color: "green", pct: 9, count: 2081 },
    { id: "direct", label: "Direct / unattributed", color: "cyan", pct: 14, count: 3237 },
  ],
};

const DATE_RANGE_METRICS: Record<DateRange, { attributedPct: number; touches: number }> = {
  "Last 7 days": { attributedPct: 84, touches: 2.8 },
  "Last 30 days": { attributedPct: 86, touches: 3.4 },
  "Last 90 days": { attributedPct: 86, touches: 3.9 },
  "Year to date": { attributedPct: 86, touches: 4.6 },
};

// A different conversion event is effectively a different, shallower or
// deeper funnel step — scales total volume and nudges the attributed share
// (earlier-funnel events like "Add to cart" are noisier/less attributable).
const CONVERSION_EVENT_FACTORS: Record<ConversionEvent, { multiplier: number; attributedDelta: number }> = {
  "Order completed": { multiplier: 1, attributedDelta: 0 },
  Signup: { multiplier: 2.4, attributedDelta: 4 },
  "Trial started": { multiplier: 1.6, attributedDelta: 2 },
  "Add to cart": { multiplier: 4.1, attributedDelta: -6 },
};

// Per-model relative weight for each channel — "First touch" favors
// discovery channels (organic, direct), "Last touch" favors close-the-loop
// channels (paid search, email); "Linear" flattens toward an equal split
// instead of using weights at all. "Time decay" is the baseline (identity).
const MODEL_WEIGHTS: Record<Exclude<Model, "Linear">, Record<string, number>> = {
  "Time decay": { "paid-social": 1, "whatsapp-campaign": 1, "organic-search": 1, "paid-search": 1, "email-nudge": 1, direct: 1 },
  "First touch": { "paid-social": 0.7, "whatsapp-campaign": 0.9, "organic-search": 1.6, "paid-search": 0.8, "email-nudge": 0.6, direct: 1.3 },
  "Last touch": { "paid-social": 1.3, "whatsapp-campaign": 0.8, "organic-search": 0.6, "paid-search": 1.5, "email-nudge": 1.4, direct: 0.5 },
};

// Reweights a set of channel rows for the given attribution model, keeping
// the same total pct/count (just redistributed) rather than inventing new
// totals — sorted so the heaviest channel under that model reads first.
function applyModel(rows: ChannelRow[], model: Model): ChannelRow[] {
  const totalPct = rows.reduce((s, r) => s + r.pct, 0);
  const totalCount = rows.reduce((s, r) => s + r.count, 0);

  if (model === "Linear") {
    const share = 1 / rows.length;
    return rows.map((r) => ({ ...r, pct: Math.round(totalPct * share), count: Math.round(totalCount * share) }));
  }

  const weights = MODEL_WEIGHTS[model];
  const weighted = rows.map((r) => ({ row: r, weight: r.pct * (weights[r.id] ?? 1) }));
  const totalWeighted = weighted.reduce((s, w) => s + w.weight, 0) || 1;
  return weighted
    .map(({ row, weight }) => {
      const share = weight / totalWeighted;
      return { ...row, pct: Math.round(share * totalPct), count: Math.round(share * totalCount) };
    })
    .sort((a, b) => b.pct - a.pct);
}

// Per-channel campaign drill-down — only "Paid Social" was shown in the
// design; the rest follow the same shape as a reasonable fill-in. Held
// constant across date range/model/event filters — a deeper level of its
// own rather than something those top-level filters reshape too.
const CAMPAIGN_BREAKDOWN: Record<string, { label: string; pct: number; count: number }[]> = {
  "paid-social": [
    { label: "Meta — Retargeting 30d", pct: 55, count: 340 },
    { label: "Meta — Prospecting LA", pct: 25, count: 155 },
    { label: "TikTok — Spark ads", pct: 12, count: 74 },
    { label: "Meta — Catalog DPA", pct: 8, count: 49 },
  ],
  "whatsapp-campaign": [
    { label: "Broadcast — Cart reminder", pct: 48, count: 297 },
    { label: "Broadcast — Win-back 60d", pct: 32, count: 198 },
    { label: "Flow — Order updates", pct: 20, count: 123 },
  ],
  "organic-search": [
    { label: "Google", pct: 68, count: 420 },
    { label: "Bing", pct: 22, count: 136 },
    { label: "DuckDuckGo", pct: 10, count: 62 },
  ],
  "paid-search": [
    { label: "Google Ads — Brand", pct: 44, count: 272 },
    { label: "Google Ads — Generic", pct: 36, count: 222 },
    { label: "Microsoft Ads", pct: 20, count: 124 },
  ],
  "email-nudge": [
    { label: "Klaviyo — Abandoned cart", pct: 61, count: 377 },
    { label: "Klaviyo — Post-purchase", pct: 39, count: 241 },
  ],
};

const Dropdown: React.FC<{ icon?: React.ReactNode; label: string; options: string[]; onChange?: (v: string) => void }> = ({
  icon,
  label,
  options,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(label);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
      >
        {icon}
        {value}
        <ChevronDown size={14} className={`text-gray-400 dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 min-w-full w-max rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                setValue(opt);
                setOpen(false);
                onChange?.(opt);
              }}
              className={`block w-full text-left px-3.5 py-2 text-sm whitespace-nowrap hover:bg-gray-50 dark:hover:bg-gray-800 ${
                opt === value ? "text-indigo-600 dark:text-indigo-400 font-semibold" : "text-gray-700 dark:text-gray-200"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ChannelList: React.FC<{
  rows: ChannelRow[];
  onSelect?: (row: ChannelRow) => void;
}> = ({ rows, onSelect }) => (
  <div className="space-y-4">
    {rows.map((row) => (
      <button
        key={row.id}
        type="button"
        onClick={() => onSelect?.(row)}
        disabled={!onSelect}
        className={`block w-full text-left group ${onSelect ? "cursor-pointer" : "cursor-default"}`}
      >
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span
            className={`text-sm font-medium text-gray-800 dark:text-gray-100 ${
              onSelect ? "group-hover:text-indigo-600 dark:group-hover:text-indigo-400" : ""
            }`}
          >
            {row.label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div className={`h-full rounded-full ${COLOR_BAR[row.color]}`} style={{ width: `${Math.min(row.pct, 100)}%` }} />
          </div>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-9 text-right shrink-0">{row.pct}%</span>
          <span className="text-xs font-medium text-gray-400 dark:text-gray-500 w-10 text-right shrink-0">{row.count}</span>
        </div>
      </button>
    ))}
  </div>
);

const AttributionPage: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>("Last 30 days");
  const [conversionEvent, setConversionEvent] = useState<ConversionEvent>("Order completed");
  const [model, setModel] = useState<Model>("Time decay");
  const [drilldown, setDrilldown] = useState<ChannelRow | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareModelLeft, setCompareModelLeft] = useState<Model>("Time decay");
  const [compareModelRight, setCompareModelRight] = useState<Model>("First touch");

  const baseChannels = DATE_RANGE_CHANNELS[dateRange];
  const baseChannelsExDirect = useMemo(() => baseChannels.filter((r) => r.id !== "direct"), [baseChannels]);
  const mainChannels = useMemo(() => applyModel(baseChannels, model), [baseChannels, model]);
  const channelRows = mainChannels.filter((r) => r.id !== "direct");
  const directRow = mainChannels.find((r) => r.id === "direct")!;

  const factor = CONVERSION_EVENT_FACTORS[conversionEvent];
  const rangeMetrics = DATE_RANGE_METRICS[dateRange];
  const totalConversions = Math.round(baseChannels.reduce((s, r) => s + r.count, 0) * factor.multiplier);
  const attributedPct = Math.max(0, Math.min(100, rangeMetrics.attributedPct + factor.attributedDelta));
  const unattributedPct = 100 - attributedPct;

  const METRICS = [
    { key: "conversions", icon: Filter, label: "Conversions", value: totalConversions.toLocaleString() },
    { key: "attributed", icon: Flame, label: "Attributed", value: `${attributedPct}%` },
    { key: "unattributed", icon: Snowflake, label: "Unattributed", value: `${unattributedPct}%` },
    { key: "touches", icon: Sparkles, label: "Avg touches / conversion", value: rangeMetrics.touches.toFixed(1) },
  ];

  const drilldownRows: ChannelRow[] = useMemo(() => {
    if (!drilldown) return [];
    return (CAMPAIGN_BREAKDOWN[drilldown.id] || []).map((c, idx) => ({
      id: `${drilldown.id}-${idx}`,
      label: c.label,
      color: drilldown.color,
      pct: c.pct,
      count: c.count,
    }));
  }, [drilldown]);

  const notify = (msg: string) => toast(msg);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-5">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Attribution</h1>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2.5">
          <Dropdown
            label="Order completed"
            options={CONVERSION_EVENTS}
            onChange={(v) => setConversionEvent(v as ConversionEvent)}
          />
          <Dropdown label="Time decay" options={MODELS} onChange={(v) => setModel(v as Model)} />
          <Dropdown
            icon={<span className="text-gray-400 dark:text-gray-500">📅</span>}
            label="Last 30 days"
            options={DATE_RANGES}
            onChange={(v) => setDateRange(v as DateRange)}
          />

          <button
            type="button"
            onClick={() => setCompareOpen((s) => !s)}
            className={`flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium border rounded-xl transition-colors ${
              compareOpen
                ? "border-indigo-300 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400"
                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
          >
            Compare <ArrowLeftRight size={14} />
          </button>

          <div className="ml-auto flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => notify("Export started — you'll get a link when it's ready.")}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors"
            >
              Export <Upload size={14} />
            </button>
            <button
              type="button"
              onClick={() => notify("Account sync isn't wired up yet.")}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors whitespace-nowrap"
            >
              Push to add accounts <CloudUpload size={14} />
            </button>
          </div>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {METRICS.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.key} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
                <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-2">
                  <Icon size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  <span className="truncate">{m.label}</span>
                </div>
                <p className="text-2xl sm:text-3xl font-semibold text-indigo-600 dark:text-indigo-400 underline decoration-2 underline-offset-4">
                  {m.value}
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5">↑ {dateRange}</p>
              </div>
            );
          })}
        </div>

        {/* Channel & campaign breakdown */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                Channel &amp; campaign breakdown
              </h2>
              {drilldown && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-full">
                  {drilldown.label}
                  <button type="button" onClick={() => setDrilldown(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-indigo-500" /> Paid acquisition
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-lime-600" /> Owned messaging
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Organic / unattributed
              </span>
            </div>
          </div>

          {drilldown ? (
            <ChannelList rows={drilldownRows} />
          ) : (
            <>
              <ChannelList rows={channelRows} onSelect={setDrilldown} />
              <div className="border-t border-dashed border-gray-200 dark:border-gray-700 my-4" />
              <ChannelList rows={[directRow]} />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                A data gap, not a channel — kept out of the ranked list and never redistributed across models.
              </p>
            </>
          )}
        </div>

        {/* Model comparison */}
        {compareOpen && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Model comparison</h2>
              <button
                type="button"
                onClick={() => setCompareOpen(false)}
                className="flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Hide <EyeOff size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Dropdown label="Time decay" options={MODELS} onChange={(v) => setCompareModelLeft(v as Model)} />
                <div className="mt-4">
                  <ChannelList rows={applyModel(baseChannelsExDirect, compareModelLeft)} />
                </div>
              </div>
              <div>
                <Dropdown label="First touch" options={MODELS} onChange={(v) => setCompareModelRight(v as Model)} />
                <div className="mt-4">
                  <ChannelList rows={applyModel(baseChannelsExDirect, compareModelRight)} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttributionPage;
